import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HandleError, HttpErrorHandler, HttpUtility } from '@core/http';
import { SdohSubmission, FormConfig, SdohReferral, VisitResponse, VisitEdit } from '@core/model';
import { FormBaseData } from '@core/model/form-io';
import { IPageResult } from '@mynexus/mynexus-ui-lib';
import { Observable, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { API_BASE_URL } from './configuration.service';

@Injectable({
  providedIn: 'root'
})
export class AssessmentService {

  public downloadSubmissionState = new Subject<boolean>();
  public handleError: HandleError;

  constructor(
    private httpUtility: HttpUtility,
    private httpErrorHandler: HttpErrorHandler,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {
    this.handleError = this.httpErrorHandler.createHandleError('AssessmentService');
  }

  public updateVisit(
    tenantId: string,
    referralId: string,
    id: string,
    body: VisitEdit
  ): Observable<VisitResponse> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/referrals/${referralId}/visits/${id}`;
    return this.httpUtility
      .put<VisitResponse>(url, body)
      .pipe(catchError(this.handleError('updateVisit', {} as VisitResponse)));
  }

  public createSubmissions(
    tenantId: string,
    referralId: string,
    data: FormBaseData,
    isDraft: boolean,
  ): Observable<SdohSubmission> {
    return this.httpUtility
      .post<SdohSubmission>(
        `${this.apiBaseUrl}/tenants/${tenantId}/referrals/${referralId}/submissions?draft=${isDraft}`,
        data
      )
      .pipe(catchError(this.handleError('createSubmissions', {} as SdohSubmission)));
  }

  public getSubmission(
    tenantId: string,
    referralId: string,
    submissionId: string
  ): Observable<FormConfig> {
    return this.httpUtility
      .get<FormConfig>(
        `${this.apiBaseUrl}/tenants/${tenantId}/referrals/${referralId}/submissions/${submissionId}/form`
      )
      .pipe(catchError(this.handleError('getSubmission', {} as FormConfig)));
  }

  public getSubmissionFile(
    tenantId: string,
    referralId: string,
    submissionId: string
  ): Observable<HttpResponse<Blob> | undefined> {
    const url = `${this.apiBaseUrl}/tenants/${tenantId}/referrals/${referralId}/submissions/${submissionId}/download`;
    return this.httpUtility
      .getBlob(url)
      .pipe(catchError(this.handleError('getSubmissionFile', undefined)));
  }

  public getReferrals(query: string = ''): Observable<IPageResult<SdohReferral>> {
    const url = `${this.apiBaseUrl}/referrals/$query`;
    return this.httpUtility
      .post<IPageResult<SdohReferral>>(url, query)
      .pipe(catchError(this.handleError('getReferrals', {} as IPageResult<SdohReferral>)));
  }

  public getArchivedReferrals(query: string = ''): Observable<IPageResult<SdohReferral>> {
    const url = `${this.apiBaseUrl}/referrals/archived/$query`;
    return this.httpUtility
      .post<IPageResult<SdohReferral>>(url, query)
      .pipe(catchError(this.handleError('getArchivedReferrals', {} as IPageResult<SdohReferral>)));
  }

  public loadReferral(tenantId: string, referralId: string): Observable<any> {
    return this.httpUtility
      .getBlob(`${this.apiBaseUrl}/tenants/${tenantId}/referrals/${referralId}/download`)
      .pipe(catchError(this.handleError('loadReferral', undefined)));
  }
}
